import { FC, ReactNode } from "react";

import { Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardChip } from "@/dashboard-design-system/DashboardChip";
import { Sparks as SparksIcon } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

export type DescriptionWizardMode = "generate" | "manual";

interface ModeTabsProps {
  mode: DescriptionWizardMode;
  onModeChange: (mode: DescriptionWizardMode) => void;
}

export const ModeTabs: FC<ModeTabsProps> = ({ mode, onModeChange }) => {
  const { t } = useTranslate();

  return (
    <Stack
      direction="row"
      gap={1}
      sx={(theme) => ({
        width: "100%",
        padding: 0.5,
        borderRadius: 1,
        backgroundColor: theme.palette.surface.neutral.quiet,
      })}
    >
      <TabButton
        active={mode === "generate"}
        icon={<SparksIcon />}
        label={
          <Stack component="span" direction="row" alignItems="center" gap={1}>
            {t("dashboard", "liveFormEditor.descriptionWizard.generateTab")}
            <DashboardChip
              component="span"
              label={t("dashboard", "new")}
              vibe="brand"
              sx={{ flexShrink: 0, textTransform: "uppercase" }}
            />
          </Stack>
        }
        dataTest="description-wizard-generate-tab"
        onClick={() => onModeChange("generate")}
      />
      <TabButton
        active={mode === "manual"}
        label={t("dashboard", "liveFormEditor.descriptionWizard.manualTab")}
        dataTest="description-wizard-manual-tab"
        onClick={() => onModeChange("manual")}
      />
    </Stack>
  );
};

const TabButton: FC<{
  active: boolean;
  label: ReactNode;
  dataTest: string;
  icon?: IconElement;
  onClick: () => void;
}> = ({ active, label, dataTest, icon, onClick }) => (
  <DashboardButton
    variant="text"
    vibe="neutral"
    size="medium"
    startIcon={icon}
    data-test={dataTest}
    onClick={onClick}
    sx={(theme) => ({
      flex: 1,
      height: 36,
      borderRadius: 1,
      backgroundColor: active ? theme.palette.surface.neutral.white : "transparent",
      color: active ? theme.palette.text.brand.intense : theme.palette.text.neutral.quiet,
      "&:hover": {
        backgroundColor: active ? theme.palette.surface.neutral.white : theme.palette.surface.neutral.moderate,
      },
    })}
  >
    {label}
  </DashboardButton>
);
